import {
  createContext,
  useContext,
  useMemo,
  useRef,
  type ReactNode,
} from 'react';

import type { LitState } from '@frontend/api';

type ViewerStateGetter = () => LitState;

/**
 * The live camera of the CAD viewer, which only the viewer component holds: it
 * changes every frame of a drag, so it is never put in redux.
 */
export type CadViewerCamera = {
  /** The mounted viewer's camera now, or `null` when no viewer is mounted. */
  getViewerState: () => LitState | null;
  /** The viewer's side: offers its camera, and returns how to withdraw it. */
  registerViewerStateGetter: (getter: ViewerStateGetter) => () => void;
};

const NO_CAMERA: CadViewerCamera = {
  getViewerState: () => null,
  registerViewerStateGetter: () => () => undefined,
};

const CadViewerCameraContext = createContext<CadViewerCamera>(NO_CAMERA);

/** Lets anything under it read the camera of the CAD viewer mounted under it. */
export const CadViewerCameraProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const getterRef = useRef<ViewerStateGetter | null>(null);

  const camera = useMemo<CadViewerCamera>(
    () => ({
      getViewerState: () => getterRef.current?.() ?? null,
      registerViewerStateGetter: (getter) => {
        getterRef.current = getter;
        return () => {
          // A remounted viewer may have registered before this one withdrew.
          if (getterRef.current === getter) {
            getterRef.current = null;
          }
        };
      },
    }),
    [],
  );

  return (
    <CadViewerCameraContext.Provider value={camera}>
      {children}
    </CadViewerCameraContext.Provider>
  );
};

export const useCadViewerCamera = () => useContext(CadViewerCameraContext);
